import type { UniverseReference, User } from '@wecasa/common/src/types/commons';
import { getCurrentCountry } from '@wecasa/common/src/utils/country';
import { formatPercentage, formatPricePerHour, formatPricePerMonth } from '@wecasa/common/src/utils/formatting';
import { isCleaningReference } from '@wecasa/common/src/utils/universes';

import { translate } from '@wecasa/i18n';

import { MEMBERSHIP_CONFIG_BY_COUNTRY } from '../constants/country-config';
import { hasPaidMembership, hasRecurrentMembership } from './membership';

const t = translate('modals.membership_paywall');

export const getMembershipAdvantages = () => {
  const membershipConfig = MEMBERSHIP_CONFIG_BY_COUNTRY[getCurrentCountry()];

  return [
    t('.advantages.wellness.prestation_reductions', {
      reductionsInPercentage: formatPercentage(membershipConfig.wellnessReductionsPercentage),
    }),
    t('.advantages.cleaning.prestation_reductions', {
      pricePerHour: formatPricePerHour(membershipConfig.cleaningMaxPricePerHour, { precision: 0 }),
    }),
    t('.advantages.wellness.free_month', { pricePerMonth: formatPricePerMonth(membershipConfig.monthlyPrice) }),
    t('.advantages.wellness.cancel_anytime'),
  ];
};

const formatMembershipQuestions = (questions: string[]) =>
  questions.map((question) => ({
    content: t(`.faq.${question}.answer`),
    title: t(`.faq.${question}.question`),
  }));

type GetMembershipQuestionsParams = {
  universeReference: UniverseReference;
  user?: User;
  isSpotCleaning?: boolean;
};

export const getMembershipQuestions = ({ universeReference, user, isSpotCleaning }: GetMembershipQuestionsParams) => {
  if (isCleaningReference(universeReference)) {
    if (hasPaidMembership(user)) {
      return formatMembershipQuestions(['what_is_wecasa_club', 'what_if_cancelling_recurrence']);
    }

    if (hasRecurrentMembership(user)) {
      return formatMembershipQuestions([
        'what_is_wecasa_club',
        'when_active_subscription_recurring',
        'what_if_cancelling_recurrence',
      ]);
    }

    if (isSpotCleaning) {
      return formatMembershipQuestions([
        'what_is_wecasa_club',
        'when_active_subscription',
        'when_monthly_payment',
        'how_cancel_wecasa_club',
      ]);
    }

    if (!isSpotCleaning) {
      return formatMembershipQuestions([
        'what_is_wecasa_club',
        'when_active_subscription_recurring',
        'what_if_cancelling_recurrence',
      ]);
    }
  }

  // wellness
  return formatMembershipQuestions([
    'what_is_wecasa_club',
    'when_active_subscription',
    'when_monthly_payment',
    'how_cancel_wecasa_club',
  ]);
};
